<template>
 <div class="tableWithPerPageSelection">
   <div  class="entete border-entete">
      <div class="is-paddingless section">
        <div class="level">
          <div class="level-left">
            <div class="columns is-gapless">
              <div class="column">
                <h1 class="title is-size-3">
                  {{ $t('previsionnelDepart.title') }}
                </h1>
                <breadcrumb :breadcrumb="breadcrumb"/>
              </div>
            </div>
          </div>
          <div class="level-right"></div>
        </div>
      </div>
   </div>
   <div class="columns is-multiline level mgt-20px">
     <div class="level-left">
       <div class="column is-narrow">
          <form @submit.prevent="recherche()">
            <b-input
              v-model="criteresRecherche.auto"
              :placeholder="$t('general.button.search')"
              type="search" icon="magnify">
            </b-input>
          </form>
        </div>
        <div class="column is-narrow">
          <button class="button" @click="setShowFiltre">
            <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
            <span class="is-size-7">
              {{$t('previsionnelDepart.actions.filtre')}}
              <label
                :class="nombreCritere > 0 ? '' : 'is-invisible'"
                class="has-text-weight-bold">
                ({{ nombreCritere }})
              </label>
            </span>
          </button>
          <span
            @click="reinitialiserFiltre()"
            class="info-circle-right is-pulled-right is-cursor-pointer">
            <b-icon icon="close-circle" type="is-danger"></b-icon>
          </span>
        </div>
     </div>
     <div class="level-right">
       <div class="column is-narrow">
          <exportExcelButton
            :perm="constantes.PREVISIONNEL_DEPART.exporter"
            :query="exportExcelQuery"/>
        </div>
        <parametrage-table
          :reference-table="referenceTable"
          @endLoadParamtre="startArrayRendering = true">
        </parametrage-table>
     </div>
   </div>
   <v-server-table
      v-if="startArrayRendering && mountedLoadingEnd"
      :columns="columns"
      :options="options"
      :ref="referenceTable"
      @loaded="finLoading"
      @loading="debutLoading">
      <template #afterTable>
        <table-items-in-view
          :perPage="resultPerPage"
          @input="$refs[referenceTable].setLimit($event)"/>
      </template>
      <template #[`devisCommande.code`]="{ row }">
        <router-link-blank
          v-if="getRouteDevisCommande(row)"
          v-bind="getRouteDevisCommande(row)">
        </router-link-blank>
      </template>
      <template #jourDepart="{ row }">
        {{ getJourDeLaSemaine(row) }}
      </template>
      <template #annonceurs="{ row }">
        {{ getAnnonceurs(row.devisCommande.intervenants) }}
      </template>
      <template #dateDebut="{ row }">
        <b-tooltip
          v-if="!isRegroupement(row.typeProduit)
            && row.dateDebut !== row.datePlanifieeLogistiqueAffichage"
          :label="$t('previsionnelDepart.info.dateAffichageDiffDatePlanifie', {
            dateDebut: formatIsoDate(row.dateDebut),
            datePlanifieeLogistiqueAffichage:
              formatIsoDate(row.datePlanifieeLogistiqueAffichage)
          })"
          type="is-warning" position="is-left">
          <b-icon icon="alert" size="is-small" type="is-warning"></b-icon>
          <span v-if="row.datePlanifieeLogistiqueAffichage">
            {{ formatDate(row.datePlanifieeLogistiqueAffichage) }}
          </span>
          <span v-else>
            {{ formatDate(row.dateDebut) }}
          </span>
        </b-tooltip>
        <div v-else>
          <span v-if="row.datePlanifieeLogistiqueAffichage">
            {{ formatDate(row.datePlanifieeLogistiqueAffichage) }}
          </span>
          <span v-else>
            {{ formatDate(row.dateDebut) }}
          </span>
        </div>
      </template>
      <template #dateFin="{ row }">
        {{ formatDate(row.dateFin) }}
      </template>
      <template #semaine="{ row }">
        {{ getSemaine(row) }}
      </template>
      <template #facesVendues="{ row }">
        <span class="is-pulled-right">
          {{ getQteFacesVendues(row) }}
        </span>
      </template>
      <template #typeProduit="{ row }">
        {{ $t(getLibelleTypeProduit(row.typeProduit)) }}
      </template>
      <template #ville="{ row }">
        {{ getVille(row) }}
      </template>
      <template #commercial="{ row }">
        {{ getUserLibelle(row.devisCommande.commercialId) }}
      </template>
      <template #assistant="{ row }">
        {{ getUserLibelle(row.devisCommande.assistantId) }}
      </template>
      <template #agence="{ row }">
        {{ getAgenceLibelle(row.produit.agenceId) }}
      </template>
      <template #[`devisCommande.type`]="{ row }">
        {{ $t(getTypeDcf(row.devisCommande.type)) }}
      </template>
      <template #afficheLivre="{ row }">
        <checkbox-async
          v-model="row.afficheLivre"
          :id="row.id"
          :maj="majAffichelivre"
          :tooltip-position="'is-left'">
        </checkbox-async>
      </template>
      <template #instructionPosePj="{ row }">
        <b-tooltip
          size="is-large"
          position="is-left"
          type="is-info"
          :active="!!row.devisCommande.instructionPosePj"
          :label="$t('previsionnelDepart.actions.telechargerPjInstructionPose')">
          <div
            class="is-cursor-pointer"
            :class="{'disabled': !row.devisCommande.instructionPosePj}"
            @click="downloadPjByUrl(row.devisCommande.instructionPosePj)">
            <b-icon
              :type="!row.devisCommande.instructionPosePj ? '' : 'is-primary'"
              icon="file-download">
            </b-icon>
          </div>
        </b-tooltip>
      </template>
   </v-server-table>
   <transition
      name="custom-classes-transition"
      enter-active-class="animate__animated animate__slideInRight"
      leave-active-class="animate__animated animate__slideOutRight"
      :duration="10">
      <div class="componentBrief" v-show="showFiltre === true">
        <filtre-liste-previsionnel-depart
          @toggleFiltre="setShowFiltre"
          ref="filtreListePrevisionnel"
          @recherche="makeRechercheFilter()"/>
      </div>
    </transition>
 </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { affichelivreAsync } from '@/api/ligneCommande';
import ucFirst from '@/services/general/ucFirst';
import formatIsoDate from '@/services/general/formatIsoDate';
import formatDate from '@/services/general/formatDate';
import getAnneeSemaineFromDate from '@/services/general/getAnneeSemaineFromDate';
import subscribeMutation from '@/services/store/subscribeMutation';
import messageErreur from '@/services/general/messageErreur';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import * as devisConf from '@/configuration/devisConf';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import { REFERENCE_TABLE_PREVISIONNEL_DEPART } from '@/configuration/general/referenceTableConf';
import { PREVISIONNEL_DEPART } from '@/configuration/logistique/permissionsLogistiqueConf';
import { CATEGORIE_REGROUPEMENT, CATEGORIE_PRODUIT, CATEGORIE_UNITAIRE } from '@/configuration/catalogue/produitConf';
import * as clientTableConf from '@/configuration/clientTableConf';
import breadcrumb from '@/components/general/breadcrumb';
import parametrageTable from '@/components/general/parametrageTable';
import TableItemsInView from '@/components/general/TableItemsInView';
import { LISTE_INDEX_JOURS } from '@/configuration/datePickerConf';
import updateCritereWithParametreInterne from '@/services/dcf/updateCritereWithParametreInterne';
import { SET_CRITERE_PREVISIONNEL_CBA } from '@/store/modules/ligneCommande-mutation-type';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import filtreListePrevisionnelDepart from '@/components/logistique/previsionnelDepart/filtreListePrevisionnelDepartCBA';
import checkboxAsync from '@/components/general/checkboxAsync';

const Societe = createNamespacedHelpers('societe');
const LignesCommandes = createNamespacedHelpers('ligneCommande');
const Parametres = createNamespacedHelpers('parametres');

export default {
  data() {
    return {
      constantes: {
        PREVISIONNEL_DEPART,
      },
      unsubscribeValue: null,
      showFiltre: false,
      startArrayRendering: false,
      mountedLoadingEnd: false,
      referenceTable: REFERENCE_TABLE_PREVISIONNEL_DEPART,
      resultPerPage: clientTableConf.RESULTS_PER_PAGE,
    };
  },
  components: {
    breadcrumb,
    TableItemsInView,
    routerLinkBlank,
    filtreListePrevisionnelDepart,
    parametrageTable,
    exportExcelButton,
    checkboxAsync,
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
      agenceActive: 'agenceActive',
      lockMenu: 'lockMenu',
      societeActive: 'societeActive',
      agencesSelectionnees: 'agenceActive',
    }),
    ...LignesCommandes.mapGetters({
      previsionnelDeparts: 'items',
      totalCount: 'totalCount',
      lastPayload: 'lastPayload',
      criteresRecherche: 'criteresRecherchePrevisionnelCBA',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('previsionnelDepart.title'), route: '', isActive: true },
      ];
    },
    options() {
      return {
        sortIcon: clientTableConf.SORT_ICON,
        headings: {
          'devisCommande.code': this.$t('previsionnelDepart.tab.numCommande'),
          commercial: this.$t('previsionnelDepart.tab.commercial'),
          assistant: this.$t('previsionnelDepart.tab.assistant'),
          agence: this.$t('previsionnelDepart.tab.agence'),
          annonceurs: this.$t('previsionnelDepart.tab.annonceur'),
          'devisCommande.theme': this.$t('previsionnelDepart.tab.theme'),
          libelle: this.$t('previsionnelDepart.tab.produit'),
          instructionPose: this.$t('previsionnelDepart.tab.instructionPose'),
          ville: this.$t('previsionnelDepart.tab.ville'),
          dateDebut: this.$t('previsionnelDepart.tab.dateDebutAffichage'),
          dateFin: this.$t('previsionnelDepart.tab.dateFinAffichage'),
          semaine: this.$t('previsionnelDepart.tab.semaine'),
          jourDepart: this.$t('previsionnelDepart.tab.jourDepart'),
          facesVendues: this.$t('previsionnelDepart.tab.nbFacesVendues'),
          typeProduit: this.$t('previsionnelDepart.tab.nature'),
          'devisCommande.type': this.$t('previsionnelDepart.tab.type'),
          afficheLivre: this.$t('previsionnelDepart.tab.afficheLivre'),
          instructionPosePj: '',
        },
        filterable: false,
        perPageValues: clientTableConf.NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: clientTableConf.TEXTS,
        requestFunction: ((data) => {
          const agencesIdsActive = this.agenceActive.map(a => a.agenceId);
          if (this.criteresRecherche.agencesIdProduit.some(agenceId => {
            return !agencesIdsActive.includes(agenceId);
          })) {
            this.criteresRecherche.agencesIdProduit = [];
          }
          if (this.criteresRecherche.agencesIdProduit.length === 0) {
            this.criteresRecherche.agencesIdProduit = this.agenceActive
              .map((agence) => agence.agenceId);
          }
          data.query = cloneDeep(this.criteresRecherche);
          data.query.societeId = this.societeActiveId;
          data.query.typesProduits = [CATEGORIE_REGROUPEMENT];
          data.withInstructionPosePj = true;
          data.query.dates.dateDebut = formatIsoDate(data.query.dates.dateDebut);
          data.query.dates.dateFin = formatIsoDate(data.query.dates.dateFin);
          data.query.dateAnneeSemaine = formatIsoDate(data.query.dateAnneeSemaine);
          return this.loadLigneCommande(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.previsionnelDeparts,
            count: this.totalCount,
          };
        }),
        columnsClasses: {
          affecter: 'is-paddingless',
          afficheLivre: 'is-relative',
        },
        sortable: [
          'devisCommande.code', 'devisCommande.theme', 'devisCommande.type',
          'libelle', 'instructionPose',
          'typeProduit', 'dateDebut',
          'dateFin',
        ],
      };
    },
    columns() {
      return [
        'devisCommande.code', 'commercial', 'assistant', 'annonceurs', 'devisCommande.theme', 'devisCommande.type',
        'libelle', 'agence', 'instructionPose', 'ville', 'typeProduit', 'dateDebut',
        'dateFin', 'semaine', 'jourDepart', 'facesVendues', 'afficheLivre',
        'instructionPosePj',
      ];
    },
    nombreCritere() {
      const banCritereKey = [
        'auto', 'logisticable', 'aIntegrerDansLaLogistique',
        'nePasReafficher', 'types', 'etatsObtenu', 'societeId',
        'datesPrevisionnelDepart', 'bloqueOuInterne', 'typesProduits',
        'afficheLivre', 'dates', 'qteMinFacesVendues', 'qteMaxFacesVendues',
      ];
      if (this.societeActive.agences.length === 1) banCritereKey.push('agencesIdProduit');
      let nbCritere = calculNombreCritere(this.criteresRecherche, banCritereKey);
      nbCritere += this.criteresRecherche.afficheLivre !== null ? 1 : 0;
      if (this.criteresRecherche.dates.dateDebut
        || this.criteresRecherche.dates.dateFin) nbCritere += 1;
      if (this.criteresRecherche.qteMinFacesVendues
        || this.criteresRecherche.qteMaxFacesVendues) nbCritere += 1;
      return nbCritere > 9 ? '+9' : nbCritere;
    },
    exportExcelQuery() {
      return {
        typeDonnee: 'previsionneldeparts',
        typeExport: null,
        filtres: this.lastPayload,
      };
    },
    allUser() {
      return this.parametre(PARAMETRES_KEYS.utilisateur);
    },
  },
  methods: {
    ...LignesCommandes.mapActions({
      loadLigneCommande: 'loadLigneCommande',
    }),
    ...LignesCommandes.mapMutations({
      setCritereRecherche: SET_CRITERE_PREVISIONNEL_CBA,
    }),
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    goFirstPage() {
      if (!this.$refs[this.referenceTable]) return;
      this.$refs[this.referenceTable].setPage(1);
    },
    getJourDeLaSemaine(ligne) {
      switch (ligne.typeProduit) {
        case CATEGORIE_REGROUPEMENT:
          return ucFirst(LISTE_INDEX_JOURS.find((jour) => {
            return jour.number === ligne.ligneReseau.jourDepart;
          }).libelle);
        case CATEGORIE_UNITAIRE:
          return ucFirst(LISTE_INDEX_JOURS.find((jour) => {
            return jour.number === (moment(ligne.dateDebut).weekday());
          }).libelle);
        default:
          return '';
      }
    },
    getSemaine(ligne) {
      let anneeSemaine = '';
      if (ligne.typeProduit === CATEGORIE_REGROUPEMENT) {
        anneeSemaine = ligne.ligneReseau.anneeSemaine.toString();
      } else {
        anneeSemaine = getAnneeSemaineFromDate(ligne.datePlanifieeLogistiqueAffichage)?.toString();
      }
      const week = anneeSemaine.substring(4);
      return this.$t('listeDemande.week', { week });
    },
    isRegroupement(type) {
      return type === CATEGORIE_REGROUPEMENT;
    },
    formatIsoDate(date) {
      return formatIsoDate(date);
    },
    formatDate(date) {
      return formatDate(date);
    },
    getLibelleTypeProduit(type) {
      return CATEGORIE_PRODUIT.find((el) => el.code === type)?.libelle || null;
    },
    getVille(ligneCommande) {
      switch (ligneCommande.typeProduit) {
        case CATEGORIE_REGROUPEMENT:
          return ligneCommande.produit.produitReseau.adresse.ville;
        case CATEGORIE_UNITAIRE:
          return ligneCommande.produit.produitFace.adresse.ville;
        default:
          return '';
      }
    },
    getQteFacesVendues(ligneCommande) {
      return ligneCommande.ligneReseau.qteFacesSouhaitees;
    },
    getAnnonceurs(intervenants) {
      return intervenants.filter(int => int.annonceur).map(el => el.nomCommercial).join(', ');
    },
    getUserLibelle(id) {
      return this.allUser.find((u) => u.id === id)?.decorator?.nomPrenom || null;
    },
    getAgenceLibelle(id) {
      return this.agencesSelectionnees.find((a) => a.agenceId === id)?.libelleAgence || '';
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
      this.recherche();
    },
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListePrevisionnel.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    async reinitialiserFiltre() {
      this.$refs.filtreListePrevisionnel.initializeFiltre();
      await this.$refs.filtreListePrevisionnel.viderCritere();
      this.$refs.filtreListePrevisionnel.appliquerCritere();
      this.showFiltre = false;
    },
    downloadPjByUrl(url) {
      const link = document.createElement('a');
      link.href = url;
      const segments = url.split('/');
      link.download = segments[segments.length - 1];
      link.click();
    },
    majAffichelivre(id, afficheLivre) {
      return affichelivreAsync({ id, afficheLivre });
    },
    getRouteDevisCommande(ligne) {
      if (!ligne.devisCommande?.id) return null;
      const detail = {
        message: ligne.devisCommande.code,
        to: {
          name: 'c.bodac.commandes',
          params: { id: ligne.devisCommande.id },
        },
      };
      if (ligne.devisCommande.type === devisConf.TYPE_COMMANDE && ligne.interne) detail.to.name = 'c.bodac.commandes-venteInterne';
      if (ligne.devisCommande.type === devisConf.TYPE_DEVIS) detail.to.name = 'c.bodac.devis';
      if (ligne.devisCommande.type === devisConf.TYPE_FACTURE) detail.to.name = 'c.bodac.factures';
      return detail;
    },
    getTypeDcf(type) {
      return devisConf.LISTE_TYPE_DEVIS_COMMANDE.find((el) => el.code === type)?.libelle || null;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      const critere = cloneDeep(this.criteresRecherche);
      await updateCritereWithParametreInterne(critere);
      critere.agencesIdProduit = this.agenceActive.map((agence) => agence.agenceId);
      this.setCritereRecherche(critere);
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche, this.goFirstPage);
      this.mountedLoadingEnd = true;
      this.setInformationPage(this.$t('pageLogistique.previsionelDepart.liste'));
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw (e);
    }
  },
  beforeDestroy() {
    this.unsubscribeValue();
  },
};
</script>

<style>

</style>
